import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Clock, Zap, CheckCircle2, X } from 'lucide-react';

const AutomationSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#FAF3E0] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Schluss mit stundenlanger Recherche!
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Warum Zeit mit manuellem Google-Suchen verschwenden? 
            Adloc liefert Ihnen alle Kontakte automatisch – in Sekunden statt Stunden.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Without Adloc (Manual) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-xl border-2 border-red-200"
          >
            <div className="flex items-center gap-3 mb-6">
              <X className="w-8 h-8 text-red-600" />
              <h3 className="text-2xl font-bold text-gray-900">Ohne Adloc</h3>
            </div>

            <div className="space-y-6">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1616587896595-51352538155b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxzYWxlcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzY0MzI3MDU0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Manual work"
                  className="w-full h-48 object-cover rounded-xl opacity-50"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Clock className="w-16 h-16 text-red-600" />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-600 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Stundenlange Google-Suche</p>
                    <p className="text-sm text-gray-600">Einzeln nach Unternehmen suchen, Stadt für Stadt</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-600 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Manuelles Kopieren & Einfügen</p>
                    <p className="text-sm text-gray-600">Jeden Kontakt einzeln in Excel übertragen</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-600 font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Unvollständige Daten</p>
                    <p className="text-sm text-gray-600">Viele Kontakte ohne E-Mail oder Telefon</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-red-600 font-bold">4</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Veraltete Informationen</p>
                    <p className="text-sm text-gray-600">Keine Garantie für Aktualität</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-red-200">
                <p className="text-center text-red-600 font-bold text-xl">⏱️ Zeitaufwand: 8-10 Stunden</p>
                <p className="text-center text-gray-600 text-sm mt-1">für nur 100 Kontakte</p>
              </div>
            </div>
          </motion.div>

          {/* Right: With Adloc (Automated) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-gradient-to-br from-[#2835F7] to-[#1a25c9] rounded-2xl p-8 shadow-2xl border-2 border-green-400 relative"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-400 text-gray-900 px-6 py-2 rounded-full text-sm font-bold shadow-lg">
              ✨ EMPFOHLEN
            </div>

            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-8 h-8 text-white" />
              <h3 className="text-2xl font-bold text-white">Mit Adloc</h3>
            </div>

            <div className="space-y-6">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1761195696590-3490ea770aa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhdXRvbWF0aW9uJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NjQzMjcwNjZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Automation"
                  className="w-full h-48 object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2835F7]/80 to-transparent rounded-xl" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Zap className="w-16 h-16 text-white" />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Filter auswählen</p>
                    <p className="text-sm text-white/90">Bundesland, Stadt, Branche – fertig!</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">CSV herunterladen</p>
                    <p className="text-sm text-white/90">Ein Klick – alle Kontakte in Sekunden</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Vollständige Datensätze</p>
                    <p className="text-sm text-white/90">95% E-Mail-Abdeckung, aktuelle Telefonnummern</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Immer aktuell</p>
                    <p className="text-sm text-white/90">Regelmäßig gepflegte Datenbank</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-white font-bold text-xl">⚡ Zeitaufwand: 2 Minuten</p>
                <p className="text-center text-white/90 text-sm mt-1">für 50.000+ Kontakte</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-2xl font-bold text-gray-900 mb-2">
            Sparen Sie <span className="text-[#2835F7]">480+ Stunden</span> pro Jahr!
          </p>
          <p className="text-gray-600">
            Das ist mehr als 60 Arbeitstage, die Sie in Ihr Geschäft investieren können.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AutomationSection;